import React, { useState, useEffect } from 'react';
import {
  MapPinIcon,
  ArrowPathIcon,
  PhoneIcon,
  GlobeAltIcon,
  MapIcon,
  InformationCircleIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { api, API_BASE_URL, getAuthToken } from '../services/api';
import toast from '../utils/toast';
import SearchableSelect from '../components/SearchableSelect';

const SendLocationPage = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [chatId, setChatId] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.getWhatsAppClients();
      setAgents(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching WhatsApp clients:', error);
      toast.error('Failed to load WhatsApp clients');
    } finally {
      setLoading(false);
    }
  };

  // Prepare WhatsApp clients data for SearchableSelect
  const agentOptions = agents && Object.keys(agents).length > 0
    ? Object.values(agents)
        .filter(client => client.status === 'active')
        .map((client) => ({
          value: client.id,
          label: client.agent_name || client.display_name || `Client ${client.client_id}`,
          description: client.whatsapp_number || client.client_id,
          icon: '📱',
          port: client.port,
          webhookApiKey: client.webhook_api_key || client.api_key,
          clientId: client.client_id
        }))
    : [];

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude.toString());
          setLongitude(position.coords.longitude.toString());
          toast.success('Current location detected successfully!');
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast.error('Failed to get current location. Please check your browser permissions.');
        }
      );
    } else {
      toast.error('Geolocation is not supported by this browser');
    }
  };

  const handleSendLocation = async () => {
    if (!selectedAgent || !chatId || !latitude || !longitude) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      const client = Object.values(agents).find(a => a.id === selectedAgent);
      if (!client) {
        toast.error('WhatsApp client not found');
        return;
      }

      // Use the new backend API endpoint that handles API key automatically
      const response = await api.sendLocationMessage(
        client.client_id,
        chatId,
        parseFloat(latitude),
        parseFloat(longitude),
        title || ''
      );

      toast.success('Location sent successfully!');
      setLatitude('');
      setLongitude('');
      setTitle('');
      setChatId('');
    } catch (error) {
      console.error('Error sending location:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(error.message || 'Failed to send location');
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <MapPinIcon className="h-8 w-8 text-red-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Send Location</h1>
                  <p className="text-sm text-gray-600">Share your location with WhatsApp contacts</p>
                </div>
              </div>
              <button
                onClick={fetchAgents}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <MapIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Location Details</h2>
            </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSendLocation(); }} className="px-6 py-6 space-y-6">

            {/* Agent Selection */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Select WhatsApp Agent
              </h3>

              <SearchableSelect
                label="WhatsApp Agent"
                name="client"
                options={agentOptions}
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                placeholder="Search and select a WhatsApp agent..."
                isRequired={true}
                isSearchable={true}
                isClearable={true}
              />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">WhatsApp Agent Selection</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Choose a connected WhatsApp agent to send your location. Only agents with active connections are available.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Details */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Location Information
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Chat ID <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={chatId}
                    onChange={(e) => setChatId(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                    placeholder="233554694531"
                    required
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Enter phone number in international format (without +)
                </p>
              </div>

              {/* Coordinates */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Coordinates <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="number"
                      step="any"
                      value={latitude}
                      onChange={(e) => setLatitude(e.target.value)}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="5.6037"
                      required
                    />
                    <label className="absolute -top-2 left-10 bg-white px-1 text-xs text-gray-600">Latitude</label>
                  </div>
                  <div className="relative">
                    <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="number"
                      step="any"
                      value={longitude}
                      onChange={(e) => setLongitude(e.target.value)}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="-0.1870"
                      required
                    />
                    <label className="absolute -top-2 left-10 bg-white px-1 text-xs text-gray-600">Longitude</label>
                  </div>
                </div>

                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={getCurrentLocation}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <MapPinIcon className="h-4 w-4 mr-2 text-red-500" />
                    Use Current Location
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-2 text-center">
                  Click to automatically detect your current coordinates
                </p>
              </div>

              {/* Optional Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                    placeholder="Location title"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Optional title for this location
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setSelectedAgent('');
                  setLatitude('');
                  setLongitude('');
                  setTitle('');
                  setChatId('');
                }}
                disabled={sending}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Form
              </button>

              <button
                type="submit"
                disabled={!selectedAgent || !chatId || !latitude || !longitude || sending}
                className="px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-lg hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {sending ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                    Sending Location...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    Send Location
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SendLocationPage;